import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { loadSolveEvents } from '../../../utils/firebase';
import { deriveCaseStats, formatTime, prepareChartData, chartOptions } from '../../../utils/statsUtils';
import { PRACTICE_MODES } from '../../../constants/modes';
import HomeButton from '../../common/navigation/HomeButton';
import '../../../styles/components/Statistics.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function Statistics() {
  const { mode, algorithmId } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getModeTitle = (mode) => {
    switch (mode) {
      case PRACTICE_MODES.OLL.EXECUTION:
        return 'OLL Execution';
      case PRACTICE_MODES.PLL.EXECUTION:
        return 'PLL Execution';
      case PRACTICE_MODES.OLL.RECOGNITION:
        return 'OLL Recognition';
      case PRACTICE_MODES.PLL.RECOGNITION:
        return 'PLL Recognition';
      default:
        return 'Practice';
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const events = await loadSolveEvents();
        setStats(deriveCaseStats(events, algorithmId, mode));
      } catch (err) {
        console.error('Error details:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [algorithmId, mode]);

  const title = getModeTitle(mode);

  if (loading) return <div className="stats-loading">Loading statistics...</div>;
  if (error) return <div className="stats-error">{error}</div>;
  if (!stats) return <div className="stats-empty">No statistics available</div>;

  return (
    <div>
      <div className="header-container">
        <h1 className="page-title">Statistics for {title} - {algorithmId}</h1>
        <HomeButton />
      </div>
      
      <div className="stats-container">
        <div className="stats-summary">
          <div className="stat-box">
            <h3>Total Attempts</h3>
            <p>{stats.totalAttempts || 0}</p>
          </div>
          <div className="stat-box">
            <h3>Fastest Time</h3>
            <p>{formatTime(stats.fastestTime)}</p>
          </div>
          <div className="stat-box">
            <h3>Ao5</h3>
            <p>{formatTime(stats.rollingAverage5)}</p>
          </div>
          <div className="stat-box">
            <h3>Ao12</h3>
            <p>{formatTime(stats.rollingAverage12)}</p>
          </div>
        </div>

        <div className="stats-chart">
          <Line 
            data={prepareChartData(stats.times)} 
            options={chartOptions}
          />
        </div>
      </div>
    </div>
  );
} 